import type { UrlConfigItem } from '@rio-cloud/rio-uikit/routeUtils';
import useUrlState from '@rio-cloud/rio-uikit/useUrlState';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useNavigationType } from 'react-router';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { FROM_EMAIL } from './contractDeepLinkingHook';
import { contractActions, getPaymentMethodRequired } from './redux/contract.redux';

export const PAYMENT_METHOD_REQUIRED_QUERY_PARAMETER = 'payment_method_required';

const knownContractsQueryParameters = [PAYMENT_METHOD_REQUIRED_QUERY_PARAMETER, FROM_EMAIL] as const;

const isPaymentMethodRequired = (search: string) =>
    new URLSearchParams(search).get(PAYMENT_METHOD_REQUIRED_QUERY_PARAMETER) === 'true';

const paymentMethodRequiredUrlConfig: UrlConfigItem<RootState, boolean>[] = [
    {
        key: PAYMENT_METHOD_REQUIRED_QUERY_PARAMETER,
        selector: getPaymentMethodRequired,
        action: contractActions.setPaymentMethodRequired,
        type: 'boolean',
        defaultValue: false,
        customDeserialize: value => value === 'true',
        customSerialize: value => (value ? 'true' : null),
    },
];

export function getNormalizedContractsSearch(search: string): string {
    const searchParameters = new URLSearchParams(search);
    const normalizedSearchParameters = new URLSearchParams();

    knownContractsQueryParameters.forEach(parameter => {
        if (searchParameters.get(parameter) === 'true') {
            normalizedSearchParameters.set(parameter, 'true');
        }
    });

    const normalizedSearch = normalizedSearchParameters.toString();
    return normalizedSearch ? `?${normalizedSearch}` : '';
}

export function usePaymentMethodRequiredUrlState() {
    const dispatch = useDispatch<RootDispatch>();
    const paymentMethodRequired = useSelector(getPaymentMethodRequired);
    const { search } = useLocation();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const initialPaymentMethodRequiredRef = useRef(isPaymentMethodRequired(search));
    const storeValues = useMemo(
        () => ({ [PAYMENT_METHOD_REQUIRED_QUERY_PARAMETER]: paymentMethodRequired }),
        [paymentMethodRequired]
    );

    useUrlState({
        config: paymentMethodRequiredUrlConfig,
        searchString: search,
        storeValues,
        navigationType,
        onUpdateStore: action => dispatch(action),
        onNavigate: (nextSearch, mode) =>
            navigate({ search: getNormalizedContractsSearch(nextSearch) }, { replace: mode === 'replace' }),
    });

    useEffect(() => {
        if (!isPaymentMethodRequired(search)) {
            dispatch(contractActions.setPaymentMethodRequired(false));
        }
    }, [dispatch, search]);

    useEffect(() => {
        const normalizedSearch = getNormalizedContractsSearch(search);
        if (normalizedSearch !== search) {
            navigate({ search: normalizedSearch }, { replace: true });
        }
    }, [navigate, search]);

    return {
        initialPaymentMethodRequired: initialPaymentMethodRequiredRef.current,
        paymentMethodRequired: isPaymentMethodRequired(search),
    };
}
